import type { LegalDocument } from '../types/consent'

/**
 * Versioned legal-document constants. Single source of truth for the consent
 * popup and the Privacy & legal settings page.
 *
 * To publish a new version of any document:
 *   1. Bump its `version` (e.g. 'v1' → 'v2')
 *   2. Update its `effective_date`
 *   3. Edit `content_md`
 * Anyone whose accepted version is older than the current `version` will be
 * re-prompted on next login. Old versions don't need to be kept; the audit
 * trail in `user_consents/{uid}` records which version each user accepted.
 *
 * The markdown source lives in `docs/superpowers/{privacypolicy,termsofservice}.md`.
 * The admin app does not include the DPA — that flow lives in the manager
 * app where org-admins accept on behalf of their organisation.
 */

export const TOS_VERSION = 'v1'
export const PRIVACY_VERSION = 'v1'

export const TERMS_OF_SERVICE: LegalDocument = {
  type: 'tos',
  version: TOS_VERSION,
  effective_date: '2026-04-30',
  content_md: `# Virtual Client – Terms of Service

**Effective Date:** 30 April 2026
**Last Updated:** 30 April 2026

## 1. Introduction
Welcome to Virtual Client. These Terms of Service ("Terms") govern your access to and use of the Virtual Client platform, including any websites, applications, and services we provide (collectively, the "Service").

The Service is operated by:

> Virtual Client
> Ashgrove House, Ashgrove
> Dun Laoghaire, Co. Dublin, A96N9K0
> Ireland

By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you may not use the Service.

## 2. Eligibility
You must be at least 18 years old to use the Service.

By creating an account, you confirm that:
- You are 18 years of age or older
- You have the legal capacity to enter into these Terms
- The information you provide is accurate and complete

If you are using the Service on behalf of an organisation, you confirm that you are authorised to bind that organisation to these Terms.

## 3. Accounts
To access the Service, you must register for an account. You are responsible for:
- Maintaining the confidentiality of your login credentials
- All activity that occurs under your account
- Notifying us immediately of any unauthorised use

We may suspend or terminate accounts that violate these Terms or applicable law.

## 4. Acceptable Use
You agree not to:
- Use the Service for any unlawful purpose
- Attempt to gain unauthorised access to the Service or related systems
- Interfere with or disrupt the Service or its security
- Reverse engineer, decompile, or extract source code from the Service
- Use the Service to transmit harmful, abusive, or infringing content
- Misuse AI-generated content in ways that violate applicable laws or third-party rights

## 5. AI-Generated Content
The Service uses artificial intelligence to generate training scenarios, feedback, and insights. You acknowledge that:
- AI outputs may contain errors or inaccuracies
- AI-generated feedback is for training purposes only and does not constitute professional advice
- You are responsible for reviewing AI outputs before relying on them
- Conversation data may be processed by third-party AI providers as described in our Privacy Policy

## 6. Organisation Use
Where the Service is used through an organisation:
- The organisation acts as the Data Controller for its users' personal data
- Organisation administrators may have visibility into user activity, sessions, and feedback for training and performance purposes
- The organisation is responsible for ensuring its users comply with these Terms

## 7. Intellectual Property
The Service, including its software, design, and content (excluding user-generated content), is owned by Virtual Client and protected by intellectual property laws.

You retain ownership of content you submit to the Service. By submitting content, you grant Virtual Client a limited licence to use it solely for the purpose of providing the Service.

## 8. Privacy
Your use of the Service is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal data.

## 9. Service Availability
We aim to keep the Service available at all times but do not guarantee uninterrupted access. We may suspend, modify, or discontinue features at any time, with or without notice.

## 10. Fees
Where the Service is offered on a paid basis, fees and payment terms will be set out in a separate agreement with the organisation or user.

## 11. Termination
You may stop using the Service at any time.

We may suspend or terminate your access if:
- You breach these Terms
- We are required to do so by law
- The organisation through which you access the Service ends its use of the platform

On termination, your right to use the Service ceases immediately. Provisions that by their nature should survive termination will continue to apply.

## 12. Disclaimer of Warranties
The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied. To the maximum extent permitted by law, Virtual Client disclaims all warranties, including merchantability, fitness for a particular purpose, and non-infringement.

## 13. Limitation of Liability
To the maximum extent permitted by law, Virtual Client will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising from your use of the Service.

Our total liability for any claim related to the Service is limited to the amount you have paid us in the twelve months preceding the claim, or one hundred euros (€100), whichever is greater.

## 14. Changes to These Terms
We may update these Terms from time to time. Where changes are significant, we will notify users appropriately. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.

## 15. Governing Law
These Terms are governed by and construed in accordance with the laws of Ireland. Any disputes will be subject to the exclusive jurisdiction of the Irish courts.

## 16. Contact
If you have any questions about these Terms, you can contact:

> Virtual Client
> Ashgrove House, Ashgrove
> Dun Laoghaire, Co. Dublin, A96N9K0
> Ireland
> Email: support@virtualclient.app
`,
}

export const PRIVACY_POLICY: LegalDocument = {
  type: 'privacy',
  version: PRIVACY_VERSION,
  effective_date: '2026-04-29',
  content_md: `# Virtual Client – Privacy Policy

**Effective Date:** 29 April 2026
**Last Updated:** 29 April 2026

## 1. Who We Are
Virtual Client is a training platform operated by:

> Virtual Client
> Ashgrove House, Ashgrove
> Dun Laoghaire, Co. Dublin, A96N9K0
> Ireland

We provide AI-powered training experiences for individuals and organisations.

## 2. What Data We Collect
We only collect data necessary to provide and improve the platform.

This may include:

**Account Information**
- Name
- Email address
- Organisation (if applicable)

**Usage Data**
- Interactions within the platform
- Scenario participation
- Feedback generated

**Conversation Data**
- Text transcripts from AI interactions
- In some cases, audio or video data (where features are enabled)

**Technical Data**
- Device/browser type
- IP address
- Basic analytics data

## 3. How We Use Your Data
We use your data to:
- Provide access to the platform
- Deliver AI-based training scenarios
- Generate feedback and insights
- Improve system performance and user experience
- Ensure security and prevent misuse

We do not use your data for marketing purposes at this time.

## 4. AI & Third-Party Processing
Virtual Client uses third-party AI and infrastructure providers to deliver core functionality.

These may include:
- AI model providers (e.g. OpenAI, Google Gemini)
- Speech processing providers (e.g. Deepgram)

Your data may be processed by these providers solely for the purpose of delivering the service.

Where organisations use their own API keys or providers, data processing may occur under their selected services.

## 5. How Your Data Is Shared
Your data may be visible to your organisation (if applicable). Admins may access usage data, session activity, and feedback for training and performance purposes.

We do not sell or share your personal data with third parties for marketing.

## 6. Data Storage & Security
We take appropriate technical and organisational measures to protect your data. Your data is stored securely and access is restricted to authorised systems and personnel.

## 7. Data Retention
We retain your data only as long as necessary to:
- Provide the service
- Meet legal obligations
- Support organisational training needs

Organisations may control retention of their users' data where applicable.

## 8. Your Rights (GDPR)
Under GDPR, you have the right to:
- Access your data
- Request correction of inaccurate data
- Request deletion of your data
- Restrict or object to processing

To exercise any of these rights, contact us at: support@virtualclient.app

## 9. Cookies
We use essential cookies to operate the platform and basic analytics to improve performance. You will be notified of cookie usage when you first access the platform.

## 10. Changes to This Policy
We may update this Privacy Policy from time to time. Where changes are significant, we will notify users appropriately.

## 11. Contact
If you have any questions about this policy or your data, you can contact:

> Virtual Client
> Ashgrove House, Ashgrove
> Dun Laoghaire, Co. Dublin, A96N9K0
> Ireland
> Email: support@virtualclient.app
`,
}

export const ALL_LEGAL_DOCUMENTS = [TERMS_OF_SERVICE, PRIVACY_POLICY] as const
